import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, type DealRadarResponse } from '../api';
import { Badge, Button, Card, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Spinner, Stat, number, unitMoney, when } from '../ui';

function belowUsual(pct: number): string {
  const rounded = pct.toFixed(1).replace(/\.0$/, '');
  return `${rounded}% below`;
}

/**
 * Potential deals among the price-guide figures BrickLoop already holds. This screen deliberately reloads the
 * cache only: fresh BrickLink price-guide requests remain the user's deliberate lot refresh or nightly job.
 */
export function DealRadar() {
  const [minDiscountPct, setMinDiscountPct] = useState(20);
  const [minSoldPieces, setMinSoldPieces] = useState(1);
  const query = useQuery({
    queryKey: ['deal-radar', minDiscountPct, minSoldPieces],
    queryFn: () => api<DealRadarResponse>('/api/v1/market/deals', { query: { minDiscountPct, minSoldPieces } }),
  });
  const rows = query.data?.rows ?? [];

  return (
    <>
      <PageHeader
        title="Deal radar"
        subtitle="Potential bargains among the parts BrickLoop already watches: lowest listed price compared with the last six months of sold prices."
      />
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Minimum below usual (%)" className="w-44" hint="How much cheaper a listing must be.">
              <Input
                type="number"
                min={0}
                max={100}
                step={1}
                value={minDiscountPct}
                onChange={(e) => setMinDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              />
            </Field>
            <Field label="Minimum sold pieces" className="w-40" hint="Across the six-month guide.">
              <Input
                type="number"
                min={1}
                max={1_000_000}
                step={1}
                value={minSoldPieces}
                onChange={(e) => setMinSoldPieces(Math.max(1, Math.min(1_000_000, Math.floor(Number(e.target.value) || 1))))}
              />
            </Field>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Reload saved figures
            </Button>
          </div>
        </Card>

        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Possible deals" value={number(rows.length)} note={rows.length ? `At least ${minDiscountPct}% below their recent sold price.` : 'No cached price signal at this threshold.'} />
          <Stat label="Comparable" value={query.data ? number(query.data.comparable) : '—'} note="With usable prices and enough recent sales." />
          <Stat label="Tracked" value={query.data ? number(query.data.tracked) : '—'} note={query.data ? `${query.data.region} price guide cache.` : 'Waiting for saved figures.'} />
        </div>

        <Notice tone="amber">
          This is a price signal, not a checkout quote. BrickLink’s price guide does not tell BrickLoop the seller,
          postage, shop terms, or whether enough pieces remain at that price. “Lowest listed” is the cached figure,
          with its check time shown below.
        </Notice>

        <Card>
          {query.isPending && <Spinner />}
          {query.error && <ErrorMessage error={query.error} />}
          {query.data && rows.length === 0 && (
            <Empty>
              No tracked item meets these thresholds. A deal appears after BrickLoop has both a for-sale and a
              six-month sold guide for the same item, colour and condition.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[72rem] text-sm">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Lowest listed</th>
                    <th className="text-right">Usual sold</th>
                    <th className="text-right">Below usual</th>
                    <th className="text-right">Sold (6 months)</th>
                    <th className="text-right">For sale when checked</th>
                    <th>Guides checked</th>
                    <th>Buy need</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.itemId}-${row.colorId}-${row.condition}`}>
                      <td>
                        <Link to={`/items/${row.itemId}?tab=price-history`} className="font-medium hover:underline">
                          {row.itemNo}
                        </Link>
                        <div className="max-w-56 truncate text-xs text-slate-500">{row.itemName}</div>
                      </td>
                      <td className="whitespace-nowrap">{row.colorName}</td>
                      <td>{row.condition === 'N' ? 'New' : 'Used'}</td>
                      <td className="text-right font-medium tabular-nums">{unitMoney(row.lowestListedPrice, row.currency)}</td>
                      <td className="text-right tabular-nums">
                        {unitMoney(row.usualSoldPrice, row.currency)}
                        <div className="text-xs text-slate-500">{row.soldPriceBasis === 'quantity_weighted' ? 'quantity-weighted' : 'average'}</div>
                      </td>
                      <td className="text-right font-medium tabular-nums text-emerald-700">{belowUsual(row.discountPct)}</td>
                      <td className="text-right tabular-nums">
                        {number(row.soldPieces)}
                        <div className="text-xs text-slate-500">{row.soldEvents === null ? 'sales unknown' : `${number(row.soldEvents)} sales`}</div>
                      </td>
                      <td className="text-right tabular-nums">
                        {row.currentPieces === null ? '—' : number(row.currentPieces)}
                        <div className="text-xs text-slate-500">{row.currentListings === null ? 'listings unknown' : `${number(row.currentListings)} listings`}</div>
                      </td>
                      <td className="whitespace-nowrap text-xs text-slate-600">
                        <div>Listed: {when(row.stockFetchedAt)}</div>
                        <div className="text-slate-500">Sold: {when(row.soldFetchedAt)}</div>
                      </td>
                      <td>
                        {row.buyList ? (
                          <>
                            <Badge tone="amber">Buy {number(row.buyList.suggested)}</Badge>
                            <div className="mt-1 text-xs text-slate-500">{number(row.buyList.available)} held · {number(row.buyList.onOrder)} on order</div>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
